/**
 * frame_governor — the ADAPTIVE RENDER-SCALE governor (perf frontier 2026-09-08,
 * item 9). Pure: feed it frame intervals, ask it for a decision.
 *
 * The lever it drives is the FSR upscale ratio (engine `setRenderScale`), so
 * a step down costs fragments, not the picture: EASU reconstructs the output
 * size from a smaller scene pass. Rules (the console-DRS shape — Unity's
 * DynamicResolutionSample and the UE frame-budget defaults):
 *   · budget = the display's refresh interval (measured from the smallest
 *     intervals seen), times a headroom factor;
 *   · DOWN immediately when the recent median exceeds the budget for
 *     `downAfterMs` (one preset step: 1.3 → 1.5 → 1.7 → 2.0);
 *   · UP is PROBED, not measured: under vsync a frame interval never reports
 *     headroom, so after `upAfterMs` steady at the refresh interval the
 *     governor tries one step up; if that step goes over budget within
 *     `probeWindowMs` it steps back and doubles the next wait (capped);
 *   · a cool-down after every change so the two never oscillate.
 */

export const GOVERNOR_STEPS: readonly number[] = [1, 1.3, 1.5, 1.7, 2.0];
/** Standard display refresh intervals (ms) the estimate snaps to. */
export const REFRESH_INTERVALS_MS: readonly number[] = [30, 48, 50, 60, 72, 75, 90, 100, 120, 144, 165, 240].map((hz) => 1000 / hz);
export function snapRefresh(ms: number): number {
  let best = REFRESH_INTERVALS_MS[0], bd = Infinity;
  for (const r of REFRESH_INTERVALS_MS) { const d = Math.abs(r - ms); if (d < bd) { bd = d; best = r; } }
  return best;
}

export interface GovernorConfig {
  /** The tier's own upscale ratio (1 = no upscale); the governor never goes below it. */
  baseScale: number;
  /** Largest ratio the governor may reach. */
  maxScale: number;
  /** Frame-time budget as a multiple of the refresh interval (1.15 = 15 % headroom). */
  budgetK: number;
  /** Medians within this factor of the refresh interval count as "steady at vsync". */
  steadyK: number;
  downAfterMs: number;
  upAfterMs: number;
  cooldownMs: number;
  /** A step down this soon after a step up marks the probe failed (next wait doubles). */
  probeWindowMs: number;
}

export const DEFAULT_GOVERNOR: GovernorConfig = {
  baseScale: 1, maxScale: 2.0, budgetK: 1.15, steadyK: 1.03, downAfterMs: 2000, upAfterMs: 12000, cooldownMs: 3000, probeWindowMs: 8000,
};

export type GovernorDecision = { dir: 'down' | 'up'; scale: number } | null;

export class FrameGovernor {
  private readonly cfg: GovernorConfig;
  private readonly window: number[] = [];
  private refreshMs = 16.7;
  private refreshSamples = 0;
  private overSince = -1;
  private underSince = -1;
  private lastChange = -Infinity;
  private lastUp = -Infinity;
  private upWaitMs: number;
  scale: number;

  constructor(cfg: Partial<GovernorConfig> = {}) {
    this.cfg = { ...DEFAULT_GOVERNOR, ...cfg };
    this.scale = this.cfg.baseScale;
    this.upWaitMs = this.cfg.upAfterMs;
  }

  /** The refresh interval estimate (ms) — the 5th percentile of intervals seen. */
  get refreshInterval(): number { return this.refreshMs; }
  get budgetMs(): number { return this.refreshMs * this.cfg.budgetK; }

  /** Median frame interval over the window (ms). */
  get medianMs(): number {
    if (this.sorted.length === 0) return 0;
    return this.sorted[this.sorted.length >> 1];
  }
  /** The window's values kept sorted (binary insert / removal of the evicted sample): no slice().sort() per frame (2026-09-12). */
  private readonly sorted: number[] = [];
  private sortedInsert(v: number): void {
    let lo = 0, hi = this.sorted.length;
    while (lo < hi) { const mid = (lo + hi) >> 1; if (this.sorted[mid] < v) lo = mid + 1; else hi = mid; }
    this.sorted.splice(lo, 0, v);
  }
  private sortedRemove(v: number): void {
    let lo = 0, hi = this.sorted.length;
    while (lo < hi) { const mid = (lo + hi) >> 1; if (this.sorted[mid] < v) lo = mid + 1; else hi = mid; }
    if (this.sorted[lo] === v) this.sorted.splice(lo, 1);
  }

  /**
   * Feed one frame interval (ms) at time `nowMs`; returns a decision when the
   * scale should change (the caller applies it and the governor records it).
   */
  push(dtMs: number, nowMs: number): GovernorDecision {
    if (!(dtMs > 0) || dtMs > 250) return null; // a pause/tab switch, not a frame
    this.window.push(dtMs);
    this.sortedInsert(dtMs);
    if (this.window.length > 90) this.sortedRemove(this.window.shift()!);
    // Refresh estimate: the median of the fastest QUARTER of intervals (one
    // stray sub-frame after a stall must not set the budget), snapped to the
    // nearest standard refresh rate; only ever lowered once learned.
    if (this.refreshSamples < 300 || dtMs < this.refreshMs * 0.95) {
      const s = this.sorted;
      const q = s[Math.max(0, Math.floor(s.length * 0.125))];
      const snapped = snapRefresh(q);
      this.refreshMs = this.refreshSamples < 60 ? snapped : Math.min(this.refreshMs, snapped);
      this.refreshSamples++;
    }
    if (this.window.length < 30) return null;
    if (nowMs - this.lastChange < this.cfg.cooldownMs) { this.overSince = -1; this.underSince = -1; return null; }
    const med = this.medianMs;
    const budget = this.budgetMs;
    if (med > budget) {
      this.underSince = -1;
      if (this.overSince < 0) this.overSince = nowMs;
      if (nowMs - this.overSince >= this.cfg.downAfterMs) {
        const next = GOVERNOR_STEPS.find((s) => s > this.scale + 1e-6 && s <= this.cfg.maxScale + 1e-6);
        this.overSince = -1;
        if (next === undefined) return null;
        // A failed probe: the step up we just tried could not hold — wait longer next time.
        if (nowMs - this.lastUp < this.cfg.probeWindowMs) this.upWaitMs = Math.min(120000, this.upWaitMs * 2);
        this.scale = next; this.lastChange = nowMs;
        return { dir: 'down', scale: next };
      }
      return null;
    }
    this.overSince = -1;
    if (med <= this.refreshMs * this.cfg.steadyK) {
      if (this.underSince < 0) this.underSince = nowMs;
      if (nowMs - this.underSince >= this.upWaitMs) {
        const lower = [...GOVERNOR_STEPS].reverse().find((s) => s < this.scale - 1e-6 && s >= this.cfg.baseScale - 1e-6);
        this.underSince = -1;
        if (lower === undefined) return null;
        this.scale = lower; this.lastChange = nowMs; this.lastUp = nowMs;
        return { dir: 'up', scale: lower };
      }
    } else {
      this.underSince = -1;
    }
    return null;
  }

  /** Reset after the tier or the base ratio changes. */
  reset(baseScale: number, maxScale: number): void {
    this.cfg.baseScale = baseScale; this.cfg.maxScale = maxScale;
    this.scale = baseScale; this.window.length = 0; this.overSince = -1; this.underSince = -1; this.lastChange = -Infinity; this.lastUp = -Infinity; this.upWaitMs = this.cfg.upAfterMs;
  }
}
